import axios, { AxiosError, AxiosRequestConfig, Cancel, CancelTokenSource } from "axios";

let requestDefaults: AxiosRequestConfig = {
    baseURL: '/'
};

const NO_CONTENT_CODE: number = 204;

const MAX_RETRY_COUNT = 3;
const RETRY_STATUS_CODE = 502;
const RETRY_DELAY_MS = 500;

axios.interceptors.response.use(
    response => response,
    (error: AxiosError | Cancel) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error as Error);
        }

        const axiosError = error as AxiosError;
        const status = axiosError.response?.status;

        if (status !== RETRY_STATUS_CODE || !axiosError.config) {
            return Promise.reject(axiosError);
        }
        const method = (axiosError.config.method ?? '').toUpperCase();
        const idempotentMethods = ['GET', 'PUT', 'HEAD', 'OPTIONS'];
        if (!idempotentMethods.includes(method)) {
            return Promise.reject(axiosError);
        }
        const config = axiosError.config as AxiosRequestConfig & { _retryCount: number };
        config._retryCount = (config._retryCount ?? 0) + 1;
        if (config._retryCount > MAX_RETRY_COUNT) {
            return Promise.reject(axiosError);
        }

        return new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * config._retryCount))
            .then(() => axios(config));
    }
);

export function setupFetchDefaults(init?: Partial<AxiosRequestConfig>) {
    requestDefaults = { ...requestDefaults, ...init };
}

export function appendFetchHeaderDefaults(additionalHeaders?: Record<string, string>) {
    requestDefaults.headers = { ...requestDefaults.headers, ...additionalHeaders };
}

export type CancellablePromiseWrapper<T = void> = { promise: Promise<T>; cancelTokenSource: CancelTokenSource };
export type MaybeCancellablePromiseWrapper<T = void> = { promise: Promise<T>; cancelTokenSource?: CancelTokenSource };

export type CancellablePromiseMap<T> = {
    [id: string]: CancellablePromiseWrapper<T>;
}

const getList: CancellablePromiseMap<any> = {};
const postList: CancellablePromiseMap<any> = {};

export type RequestOptions = {
    headers?: Record<string, string>;
};

export type GetOptions = RequestOptions & {
    requestKey?: string;
};

export function get<T>(url: string, data?: any, options?: GetOptions): Promise<T> {
    return cancellableGet<T>(url, data, options).promise;
}

export function cancellableGet<T>(url: string, data?: any, options?: GetOptions): CancellablePromiseWrapper<T> {
    const key = options?.requestKey ?? url;
    const source = axios.CancelToken.source();
    const request: AxiosRequestConfig = {
        ...requestDefaults,
        cancelToken: source.token,
        headers: { ...requestDefaults.headers, ...options?.headers }
    }

    // Build query params, handling arrays properly by repeating the parameter name
    const queryParams = data && Object.getOwnPropertyNames(data)
        .filter(_ => data[_] !== undefined)
        .map(_ => {
            const value = data[_];
            if (Array.isArray(value)) {
                // For arrays, create multiple parameters with the same name
                return value.map(item => `${_}=${encodeURIComponent(item)}`).join('&');
            } else {
                return `${_}=${encodeURIComponent(value)}`;
            }
        }).join("&");
    const getUrl = `${url}${queryParams ? ("?" + queryParams) : ""}`;
    const promise = axios.get<T>(getUrl, request).then(_ => {
        delete getList[key];
        return _;
    }, err => {
        getList[key].promise === promise && delete getList[key];
        throw err;
    }).then(response => {
        return response.data;
    });

    if (getList[key]) {
        getList[key].cancelTokenSource.cancel();
    }

    const promiseWrapper: CancellablePromiseWrapper<T> = { promise, cancelTokenSource: source };

    getList[key] = promiseWrapper;

    return promiseWrapper;
}

export function post<T>(url: string, data: object, options?: RequestOptions): Promise<T> {
    const request: AxiosRequestConfig = {
        ...requestDefaults,
        headers: { ...requestDefaults.headers, ...options?.headers }
    }

    return axios.post<T>(url, data, request).then(response => response.data);
}

export function cancellablePost<T = void>(url: string, data: object): CancellablePromiseWrapper<T> {
    const source = axios.CancelToken.source();
    const request: AxiosRequestConfig = {
        ...requestDefaults,
        cancelToken: source.token
    }

    const promise = axios.post<T>(url, data, request)
        .then(_ => _.data)
        .finally(() => { delete postList[url] });

    if (postList[url]) {
        postList[url].cancelTokenSource.cancel();
    }

    const promiseWrapper: CancellablePromiseWrapper<T> = { promise, cancelTokenSource: source };
    postList[url] = promiseWrapper;
    return promiseWrapper;
}

export function remove<T>(url: string, data?: object): Promise<T> {
    const request: AxiosRequestConfig = {
        ...requestDefaults,
        method: 'delete',
    }

    if (data) {
        request.headers = {
            ...request.headers,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        };
        request.data = JSON.stringify(data);
    }

    return axios.delete(url, request).then(response => {
        if (response.status == NO_CONTENT_CODE) {
            return Promise.resolve() as any;
        }

        return response.data;
    });
}